// Reports > Staffing. Titles, descriptions, columns and notes follow Eric's staffing prototype (Reports · Staffing).
// Wage columns carry `wages: true` and are dropped unless the viewer has unrestricted read_wages; the server
// never sends those values either.
import React from 'react';
import { currencyFormatter } from '../utils/reportingUtils';

const col = (id, label, sortType, extra = {}) => ({
  id, label, alwaysVisible: false, defaultVisible: true, sortable: true, sortType, ...extra,
});

const hoursText = (value) => `${(value || 0).toFixed(1)} h`;
const money = (value) => currencyFormatter.format(value || 0);
const percentText = (value) => (value == null ? '—' : `${value}%`);
const WARNING = { color: 'var(--sl-text-warning-primary, #B54708)' };

// No prior-period badge: delta null hides TrendBadge.
const card = (label, value) => ({ label, value, current: 0, prior: 0, delta: null });

export const STAFFING_REPORTS = {
  labor_by_event: {
    title: 'Labor by event',
    description: 'Scheduled vs actual hours, labor % of gross and labor cost against the event budget for each show.',
    note: 'Unscheduled punches and shifts without a show are left out. Labor % is labor cost over gross ticket sales.',
    columns: [
      col('event_name', 'Event', 'text', { alwaysVisible: true }),
      col('start_time', 'Date', 'date'),
      col('scheduled_hours', 'Scheduled h', 'number'),
      col('actual_hours', 'Actual h', 'number'),
      col('variance_hours', 'Variance', 'number'),
      col('scheduled_cost', 'Scheduled cost', 'currency', { wages: true }),
      col('labor_cost', 'Labor cost', 'currency', { wages: true }),
      col('gross', 'Gross', 'currency', { wages: true }),
      col('labor_percent', 'Labor %', 'number', { wages: true }),
      // 'number', not 'currency': the currency renderer turns a missing budget into $0.
      col('budget', 'Budget', 'number', { wages: true }),
      col('budget_variance', 'vs budget', 'number', { wages: true }),
    ],
    defaultSort: 'start_time',
    itemLabel: (count) => `event${count !== 1 ? 's' : ''}`,
    searchPlaceholder: 'Search events...',
    kpis: (k, showWages) => [
      card('Events', (k.events || 0).toLocaleString()),
      card('Scheduled', hoursText(k.scheduled_hours)),
      card('Actual', hoursText(k.actual_hours)),
      ...(showWages ? [card('Labor cost', money(k.labor_cost)), card('Labor %', percentText(k.labor_percent))] : []),
    ],
  },
  labor_by_position: {
    title: 'Labor by position',
    description: 'Where the hours and money go: bar, security, stage and more.',
    note: 'Average cost per hour includes the overtime premium.',
    columns: [
      col('position_name', 'Position', 'text', { alwaysVisible: true }),
      col('staff_count', 'Staff', 'number'),
      col('hours', 'Hours', 'number'),
      col('overtime_hours', 'OT h', 'number'),
      col('labor_cost', 'Labor cost', 'currency', { wages: true }),
      col('average_cost_per_hour', 'Avg cost / h', 'currency', { wages: true }),
      col('share', 'Share', 'number', { sortable: false }),
    ],
    defaultSort: 'hours',
    itemLabel: (count) => `position${count !== 1 ? 's' : ''}`,
    searchPlaceholder: 'Search positions...',
    kpis: (k, showWages) => [
      card('Positions', (k.positions || 0).toLocaleString()),
      card('Staff', (k.staff_count || 0).toLocaleString()),
      card('Hours', hoursText(k.hours)),
      showWages ? card('Labor cost', money(k.labor_cost)) : card('OT h', hoursText(k.overtime_hours)),
    ],
  },
  hours_by_staff: {
    title: 'Hours by staff member',
    description: 'Who worked what, with overtime and gross pay.',
    note: 'Sorted by actual hours. Gross pay is before tips and taxes.',
    columns: [
      col('staff_member_name', 'Staff member', 'text', { alwaysVisible: true }),
      col('position_name', 'Position', 'text'),
      col('scheduled_hours', 'Scheduled h', 'number'),
      col('actual_hours', 'Actual h', 'number'),
      col('volunteer_hours', 'Volunteer h', 'number'),
      col('overtime_hours', 'OT h', 'number'),
      col('cards', 'Cards', 'number'),
      col('gross_pay', 'Gross pay', 'currency', { wages: true }),
    ],
    defaultSort: 'actual_hours',
    itemLabel: (count) => `staff member${count !== 1 ? 's' : ''}`,
    searchPlaceholder: 'Search staff...',
    kpis: (k, showWages) => [
      card('People', (k.people || 0).toLocaleString()),
      card('Actual', hoursText(k.actual_hours)),
      card('Volunteer', hoursText(k.volunteer_hours)),
      showWages ? card('Gross pay', money(k.gross_pay)) : card('OT h', hoursText(k.overtime_hours)),
    ],
  },
  volunteer_hours: {
    title: 'Volunteer hours',
    description: 'Hours volunteers gave, by person, position and show.',
    note: 'Volunteer positions carry no pay, so they never appear in labor cost.',
    columns: [
      col('staff_member_name', 'Staff member', 'text', { alwaysVisible: true }),
      col('position_name', 'Position', 'text'),
      col('event_name', 'Event', 'text'),
      col('clock_in_at', 'Date', 'date'),
      col('hours', 'Hours', 'number'),
    ],
    defaultSort: 'clock_in_at',
    itemLabel: (count) => `time card${count !== 1 ? 's' : ''}`,
    searchPlaceholder: 'Search volunteers, positions, events...',
    kpis: (k) => [
      card('Volunteers', (k.volunteers || 0).toLocaleString()),
      card('Hours', hoursText(k.hours)),
      card('Time cards', (k.cards || 0).toLocaleString()),
    ],
  },
  attendance: {
    title: 'Attendance',
    description: 'Late punches, no-shows, auto clock-outs and missed breaks.',
    note: 'Late means no punch 10 minutes after shift start. A no-show is a published shift with no time card.',
    columns: [
      col('staff_member_name', 'Staff member', 'text', { alwaysVisible: true }),
      col('shifts', 'Shifts', 'number'),
      col('on_time_rate', 'On time', 'number'),
      col('late', 'Late', 'number'),
      col('no_shows', 'No-shows', 'number'),
      col('auto_clock_outs', 'Auto clock-outs', 'number'),
      col('missed_breaks', 'Missed breaks', 'number'),
    ],
    defaultSort: 'shifts',
    itemLabel: (count) => `staff member${count !== 1 ? 's' : ''}`,
    searchPlaceholder: 'Search staff...',
    kpis: (k) => [
      card('Shifts', (k.shifts || 0).toLocaleString()),
      card('On time', percentText(k.on_time_rate)),
      card('Late', (k.late || 0).toLocaleString()),
      card('No-shows', (k.no_shows || 0).toLocaleString()),
      card('Auto clock-outs', (k.auto_clock_outs || 0).toLocaleString()),
      card('Missed breaks', (k.missed_breaks || 0).toLocaleString()),
    ],
  },
};

export const STAFFING_REPORT_KEYS = Object.keys(STAFFING_REPORTS);

const ERROR_COUNT_COLUMNS = ['late', 'no_shows', 'auto_clock_outs', 'missed_breaks'];

export const renderStaffingCell = (col, row, { formatEventDateTime }) => {
  const value = row[col.id];
  switch (col.sortType) {
    case 'currency': return money(value);
    case 'date': return <span className="text-nowrap">{formatEventDateTime(value)}</span>;
    case 'text': return value || '—';
    default: break;
  }
  if (col.id === 'variance_hours') return <span style={value > 1 ? WARNING : undefined}>{`${value >= 0 ? '+' : ''}${value.toFixed(1)}`}</span>;
  // No budget, or no punches yet, is a dash rather than $0.
  if (col.id === 'budget') return value == null ? '—' : money(value);
  if (col.id === 'budget_variance') {
    if (value == null) return '—';
    return <span style={value > 0 ? WARNING : undefined}>{`${value > 0 ? '+' : ''}${money(value)}`}</span>;
  }
  if (col.id === 'labor_percent') return <span style={value > 12 ? WARNING : undefined}>{percentText(value)}</span>;
  if (col.id === 'share' || col.id === 'on_time_rate') return percentText(value);
  if (ERROR_COUNT_COLUMNS.includes(col.id)) return <span style={value ? { color: 'var(--sl-text-error-primary, #D92D20)' } : undefined}>{value}</span>;
  if (col.id.endsWith('_hours') || col.id === 'hours') return (value || 0).toFixed(1);
  return (value ?? 0).toLocaleString();
};
